"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.safeDestroyScene = safeDestroyScene;
exports.evictSceneImagesFromCache = evictSceneImagesFromCache;
exports.destroyEvictedTextures = destroyEvictedTextures;
exports.unloadSceneImages = unloadSceneImages;
/**
 * Safely destroys a ZScene, suppressing any errors that may occur
 * during the destruction process.
 */
function safeDestroyScene(scene) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            yield scene.destroy();
        }
        catch (e) {
            // Non-critical: may happen for image-based scenes that have no spritesheet.
            // Phaser still frees the textures in the destroy loop.
        }
    });
}
/**
 * Reads the placements.json for a scene folder and returns every image alias
 * that ZScene would have registered (stripping _9S / _IMG suffixes).
 * Returns an empty array on any error or for atlas-based scenes.
 */
function _readAliases(assetBasePath) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        const res = yield fetch(assetBasePath + 'placements.json');
        if (!res.ok)
            return [];
        const data = yield res.json();
        if (data.atlas !== false)
            return [];
        const seen = new Set();
        const result = [];
        for (const tmpl of Object.values((_a = data.templates) !== null && _a !== void 0 ? _a : {})) {
            for (const child of ((_b = tmpl.children) !== null && _b !== void 0 ? _b : [])) {
                if (child.type === 'img' || child.type === '9slice') {
                    let alias = child.name;
                    if (alias.endsWith('_9S'))
                        alias = alias.slice(0, -3);
                    if (alias.endsWith('_IMG'))
                        alias = alias.slice(0, -4);
                    if (!seen.has(alias)) {
                        seen.add(alias);
                        result.push(alias);
                    }
                }
            }
        }
        return result;
    });
}
/**
 * Phase 1 — call this BEFORE loading the next page.
 *
 * Removes alias entries from Phaser's texture cache WITHOUT destroying the
 * underlying WebGL textures.  This forces the next ZScene load for those
 * aliases to download fresh textures rather than reusing old cached ones,
 * while still allowing the old page's sprites to keep rendering safely.
 *
 * Returns the evicted Texture objects.  You MUST call .destroy() on each one
 * after all sprites that referenced them have been destroyed (phase 2).
 */
function evictSceneImagesFromCache(assetBasePath, scene) {
    return __awaiter(this, void 0, void 0, function* () {
        const evicted = [];
        try {
            const aliases = yield _readAliases(assetBasePath);
            const texList = scene.textures.list;
            for (const alias of aliases) {
                if (texList[alias]) {
                    evicted.push(texList[alias]);
                    delete texList[alias]; // remove cache entry but keep GL texture alive
                }
            }
        }
        catch (_) { /* non-fatal */ }
        return evicted;
    });
}
/**
 * Phase 2 helper — call this AFTER old sprites are destroyed.
 * Destroys the Texture objects returned by evictSceneImagesFromCache,
 * freeing WebGL memory.
 */
function destroyEvictedTextures(textures) {
    for (const tex of textures) {
        try {
            tex.destroy();
        }
        catch (_) { /* already destroyed */ }
    }
}
/** @deprecated Use evictSceneImagesFromCache + destroyEvictedTextures instead. */
function unloadSceneImages(assetBasePath, scene) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!scene)
            return;
        try {
            const aliases = yield _readAliases(assetBasePath);
            for (const alias of aliases) {
                if (scene.textures.exists(alias))
                    scene.textures.remove(alias);
            }
        }
        catch (_) { /* non-fatal */ }
    });
}
